import {
  PrimaryBigButton,
  SecondaryBigButton
} from 'components/ReusableComponents/Button/BigButtons'
import DragAndDropUploader from 'components/ReusableComponents/DragAndDropUploader'
import EditableHotelList from 'components/ReusableComponents/EditableHotelList'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import AddIcon from 'components/ReusableComponents/Icons/Add'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import FormGroup from 'components/ReusableComponents/ManageHotel/FormGroup'
import PdfViewerDialog from 'components/ReusableComponents/PdfViewerDialog'
import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router'
import { Redirect } from 'react-router-dom'
import contractApi from 'services/api/contracts'

import ContactHistoryDialog from './ContactHistoryDialog'
import DocumentTile from './DocumentTile'
import styles from './EditGroupContract.module.scss'

/**
 *
 * @typedef {'documents' | 'hotels'} Step
 */

const EditIndependentContract = () => {
  const { subsidiaryId } = useParams()
  const pond = useRef()

  const isCreate = !subsidiaryId

  /**
   * @type {[Step, React.Dispatch<React.SetStateAction<Step>>]}
   */
  const [step, setStep] = useState(/** @type {Step} */ ('documents'))
  const [isLoading, setIsLoading] = useState(true)
  const [lastUpdatedOn, setLastUpdatedOn] = useState('')
  const [merchantContact, setMerchantContact] = useState()
  const [contactHistories, setContactHistories] = useState([])
  const [isContactHistoryDialogVisible, setIsContactHistoryDialogVisible] =
    useState(false)

  const [documents, setDocuments] = useState([])
  const [isUploadFileVisible, setIsUploadFileVisible] = useState(true)
  const [previewedDocument, setPreviewedDocument] = useState()

  const [hotels, setHotels] = useState([])

  const [merchantName, setMerchantName] = useState('')

  const history = useHistory()

  const getData = React.useCallback(() => {
    setIsLoading(true)

    contractApi.internalAdmin
      .get({ merchantId: -100, subsidiaryId })
      .then(response => {
        const { updatedOn, merchantName, contacts, contactHistories, documents, hotels } =
          response
        setLastUpdatedOn(updatedOn)
        setMerchantName(merchantName)
        setMerchantContact(contacts[0])
        setContactHistories(contactHistories)
        setDocuments(documents)

        if (contacts.length > 0) {
          setStep('documents')
        }
        if (documents.length > 0) {
          setStep('hotels')
          setIsUploadFileVisible(false)
        }

        if (!isCreate) setHotels(hotels)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [isCreate, subsidiaryId])

  useEffect(() => {
    getData()
  }, [getData])

  async function onFileUploadComplete(data) {
    const filePath = JSON.parse(data).result.path

    setIsUploadFileVisible(false)

    contractApi.internalAdmin
      .addNewMerchantDocument({
        merchantId: -100,
        name: filePath.split('/').pop(),
        path: filePath
      })
      .then(res => {
        setDocuments(documents => [...documents, ...res.addingMerchantContractDocuments])
      })
      .catch(console.warn)
  }

  function handleSave() {
    setIsLoading(true)

    getData()
  }

  if (isCreate && hotels.length === 1) {
    return <Redirect to={`/contracts/edit/s/${hotels[0].id}`} />
  }

  return (
    <div className={styles.wrapper}>
      {isContactHistoryDialogVisible && (
        <ContactHistoryDialog
          contacts={[
            {
              ...merchantContact,
              isActive: true
            },
            ...contactHistories
          ]}
          handleClose={() => setIsContactHistoryDialogVisible(false)}
        />
      )}
      {isLoading && <LoadingIndicator />}
      {previewedDocument && (
        <PdfViewerDialog
          pdfUrl={previewedDocument.path}
          onClose={() => setPreviewedDocument(null)}
        />
      )}
      <CustomHeader
        title={isCreate ? 'Add new contract' : hotels[0]?.hotelName}
        width={830}
        isFixed
      />

      <FormGroup
        title="Legal contract"
        disabled={step === 'merchantContacts' || isLoading}
      >
        {documents && documents.length > 0 && (
          <div className={styles.documentsWrapper}>
            {documents.map(document => (
              <DocumentTile
                key={document.id}
                document={document}
                onClick={() => setPreviewedDocument(document)}
              />
            ))}
          </div>
        )}

        {isUploadFileVisible ? (
          <div className={styles.uploadFormHolder}>
            <DragAndDropUploader
              url="/merchantcontracts/files"
              ref={pond.current}
              acceptedFileTypes={['application/pdf']}
              onData={formData => {
                formData.append('merchantId', -100)
                return formData
              }}
              onLoad={onFileUploadComplete}
              labelIdle="
              <p class='filepond-p-micro'>Drag &amp; drop file here  </p>
              <span class='filepond-or-micro'>or</span> <span class='filepond--label-action-micro'>Browse file</span>"
            />
          </div>
        ) : (
          <button
            className={styles.addNewFileButton}
            onClick={() => setIsUploadFileVisible(true)}
          >
            <AddIcon className={styles.icon} />
            <p className={styles.text}>Add another contract</p>
          </button>
        )}
      </FormGroup>
      <FormGroup
        title="Hotel"
        disabled={documents?.length === 0 || isLoading}
        contentPadding="0"
      >
        <EditableHotelList
          initialHotels={hotels}
          onChange={setHotels}
          isEditable={hotels.length === 0}
          merchantId={-100}
          showUsers
          onNewUserAdded={getData}
          contractDocuments={documents}
        />
      </FormGroup>
      <div className={styles.bottomBar}>
        <div className={styles.bottomBarContainer}>
          <p className={styles.lastSavedText}>
            Last saved on {moment(lastUpdatedOn).format('MMM DD, YYYY, hh:MM A')}
          </p>
          <div className={styles.bottomBarButtonsHolder}>
            <SecondaryBigButton onClick={() => history.push('/contracts/list')}>
              Cancel
            </SecondaryBigButton>
            <PrimaryBigButton onClick={() => handleSave()}>Save changes</PrimaryBigButton>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EditIndependentContract
